import { useState } from 'react';
import { mockAuth } from '@/data/mock';
import { Lock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggedIn, setLoggedIn] = useState(mockAuth.isAuthenticated());
  const user = mockAuth.getUser();

  const handleLogin = (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error('Please enter your credentials.');
      return;
    }
    const result = mockAuth.login(email, password);
    if (result.success) {
      setLoggedIn(true);
      toast.success('Welcome back.');
    } else {
      toast.error('Invalid credentials.');
    }
  };

  const handleLogout = () => {
    mockAuth.logout();
    setLoggedIn(false);
    setEmail('');
    setPassword('');
    toast.success('Logged out successfully.');
  };

  if (loggedIn && user) {
    return (
      <div className="bg-vine-warm min-h-[70vh]">
        <Toaster position="top-center" />
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="w-12 h-px bg-copper mb-10" />
          <span className="section-label block mb-5">Members Portal</span>
          <h1 className="font-display text-3xl md:text-4xl text-vine-primary font-light mb-2">
            Welcome back, {user.name}.
          </h1>
          <p className="text-vine-primary/50 text-[15px] mb-12">{user.tier}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'Private Pulse', desc: 'Your next session is scheduled. Camera optional.', action: 'View Schedule' },
              { title: 'Operator Lounge', desc: 'Upcoming sessions and past recordings.', action: 'Access Lounge' },
              { title: 'Boardroom Review', desc: 'Submit your quarterly review materials.', action: 'Submit Review' },
              { title: 'Resource Hub', desc: 'Operator Packets, frameworks, and templates.', action: 'Browse Resources' },
            ].map((item) => (
              <div key={item.title} className="p-8 border border-vine-neutral/20 bg-white/50 hover-lift cursor-pointer">
                <h3 className="font-display text-xl text-vine-primary mb-2">{item.title}</h3>
                <p className="text-vine-primary/55 text-[14px] leading-relaxed mb-5">{item.desc}</p>
                <span className="inline-flex items-center gap-2 text-copper text-[13px] tracking-[0.08em] uppercase">
                  {item.action} <ArrowRight size={14} />
                </span>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-vine-neutral/15">
            <button
              onClick={handleLogout}
              className="text-vine-primary/40 text-[13px] tracking-[0.05em] uppercase hover:text-copper transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-vine-warm min-h-[70vh] flex items-center justify-center">
      <Toaster position="top-center" />
      <div className="max-w-sm mx-auto px-6 w-full py-24">
        <div className="text-center mb-10">
          <Lock size={24} className="text-copper mx-auto mb-5" />
          <h1 className="font-display text-3xl text-vine-primary font-light">Members</h1>
          <p className="mt-2 text-vine-primary/50 text-[14px]">Access your advisory portal.</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="text-[13px] text-vine-primary/60 tracking-wide block mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white/60 border border-vine-neutral/25 text-vine-primary text-[15px] placeholder:text-vine-neutral/50 focus:outline-none focus:border-copper focus:ring-1 focus:ring-copper/30 transition-colors"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="text-[13px] text-vine-primary/60 tracking-wide block mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-white/60 border border-vine-neutral/25 text-vine-primary text-[15px] placeholder:text-vine-neutral/50 focus:outline-none focus:border-copper focus:ring-1 focus:ring-copper/30 transition-colors"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            className="w-full px-6 py-3.5 bg-copper text-white text-[13px] tracking-[0.12em] uppercase btn-copper mt-2"
          >
            Sign In
          </button>
        </form>

        <p className="text-center text-vine-primary/30 text-[12px] mt-8">
          Access is limited to active Advisory Circle members.
        </p>
      </div>
    </div>
  );
}
